/**
 * Builds the exact HTML used by the mobile app for PDF export (ReportDetailsNew / generatePdfAndShare).
 * Use with html2canvas + jsPDF to produce a PDF that matches the mobile app.
 */

const escapeHtml = (value: any): string => {
  const s = String(value ?? '');
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const MOBILE_CSS = `
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f5f5f5; color: #333; line-height: 1.4; }
.page { width: 816px; min-height: 1056px; background: white; margin: 0 auto; display: flex; flex-direction: column; }
.header {
  background: linear-gradient(135deg, #2c3e50 0%, #223447 55%, #1b2b3c 100%);
  color: white; padding: 20px 30px; display: flex; justify-content: space-between; align-items: center;
}
.logo-section { display: flex; align-items: center; }
.logo {
  width: 60px; height: 60px; background: white; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; margin-right: 15px;
  font-weight: bold; color: #2c3e50; font-size: 18px; overflow: hidden;
}
.logo img { width: 60px; height: 60px; object-fit: cover; display: block; }
.company-name { font-size: 40px; font-weight: 300; letter-spacing: 1px; }
.contact-info { text-align: right; font-size: 14px; line-height: 1.3; }
.main-content { padding: 28px; flex: 1; }
.report-header { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 28px; gap: 6px; }
.report-cards-row {
  width: 100%; display: flex; justify-content: space-between; align-items: stretch; gap: 24px; flex-wrap: wrap;
}
.report-heading {
  width: 100%; background: #f3f4f6; display: flex; flex-direction: column; gap: 0; padding: 0 30px;
}
.report-heading-content { min-height: auto; padding: 12px 0; display: flex; flex-direction: column; justify-content: flex-start; }
.report-separator { width: 100%; height: 1px; background: #e5e7eb; }
.report-title { flex: 1; width: 100%; text-align: center; }
.report-title h1 { font-size: 42px; font-weight: 600; color: #2c3e50; margin-bottom: 0; line-height: 1.1; white-space: nowrap; }
.alert-box {
  background: linear-gradient(180deg, #f4dfe2 0%, #f2f2f6 55%, #eeeeee 100%);
  border: 1px solid rgba(0,0,0,0.06); border-radius: 16px; padding: 0 22px 8px 22px;
  text-align: left; min-width: 280px; box-shadow: 0 10px 28px rgba(0,0,0,0.16); flex: 0 1 360px;
}
.alert-box.ok { background: linear-gradient(180deg, #ddf4e2 0%, #f2f6f2 55%, #eeeeee 100%); }
.alert-title {
  font-size: 28px; font-weight: 700; color: #b0262c; text-align: center;
  display: flex; align-items: center; justify-content: center; padding: 8px 22px;
  border-bottom: 1px solid rgba(176,38,44,0.35); margin: 0 -22px 12px -22px;
}
.alert-title.ok { color: #1f7a3a; border-bottom-color: rgba(31,122,58,0.35); }
.alert-details { font-size: 16px; color: #333; line-height: 1.35; }
.alert-details div { margin-bottom: 12px; }
.alert-details strong { color: #2c3e50; font-weight: 700; }
.site-card {
  background: linear-gradient(180deg, #cfd6de 0%, #f2f2f6 55%, #eeeeee 100%);
  border: 1px solid rgba(0,0,0,0.06); border-radius: 16px; padding: 0 22px 8px 22px;
  text-align: left; min-width: 280px; max-width: 360px; width: 100%;
  box-shadow: 0 10px 28px rgba(0,0,0,0.16); flex: 0 1 360px;
}
.site-card.full-width {
  max-width: none; border-radius: 10px;
  background: linear-gradient(180deg, #fff3cd 0%, #fff7dd 60%, #ffffff 100%); border: 1px solid #ffeaa7;
}
.site-card.full-width h2 { color: #856404; border-bottom-color: rgba(133,100,4,0.35); }
.site-card.full-width p, .site-card.full-width strong { color: #856404; }
.site-card h2 {
  font-size: 20px; font-weight: 700; color: #2c3e50; margin-bottom: 8px; text-align: center;
  display: flex; align-items: center; justify-content: center; padding: 8px 22px;
  border-bottom: 1px solid rgba(44,62,80,0.25); margin: 0 -22px 8px -22px;
}
.site-card p { margin-bottom: 8px; font-size: 16px; color: #333; }
.site-card strong { color: #2c3e50; font-weight: 700; }
.site-card-row { display: flex; align-items: flex-start; gap: 16px; }
.site-thumb { width: 110px; height: 80px; object-fit: cover; border-radius: 8px; border: 1px solid rgba(0,0,0,0.12); flex: 0 0 auto; }
.content-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-bottom: 28px; align-items: start; }
.content-grid.single { grid-template-columns: 1fr; justify-items: start; }
.image-section { text-align: center; margin: 28px 0; }
.image-section h2 { font-size: 20px; font-weight: 600; color: #2c3e50; margin-bottom: 14px; }
.image-grid { width: 100%; display: flex; flex-wrap: wrap; justify-content: center; gap: 16px; }
.image-box {
  width: 220px; height: 160px; background: #f3f4f6; border-radius: 8px; overflow: hidden;
  border: 1px solid rgba(0,0,0,0.12); display: flex; align-items: center; justify-content: center;
}
.grid-photo { width: 100%; height: 100%; object-fit: cover; display: block; }
.notes-reporting { width: 100%; display: flex; justify-content: flex-end; margin-top: 10px; }
.notes-reporting label { display: flex; align-items: flex-start; gap: 10px; cursor: default; user-select: none; }
.notes-reporting input[type="checkbox"] { width: 18px; height: 18px; margin-top: 2px; accent-color: #856404; }
.notes-reporting-text { font-size: 14px; line-height: 1.25; color: #856404; text-align: left; }
.notes-reporting-text strong { font-weight: 700; }
.case-number-value { font-weight: 400; }
.footer {
  background: linear-gradient(135deg, #2c3e50 0%, #223447 55%, #1b2b3c 100%);
  color: white; text-align: center; padding: 19px; font-size: 20px; font-weight: 500;
  margin-top: auto; display: flex; align-items: center; justify-content: center; gap: 13px; position: relative;
}
.footer-divider { opacity: 0.65; }
.footer-site { position: absolute; right: 16px; bottom: 10px; font-size: 14px; font-weight: 500; opacity: 0.9; white-space: nowrap; }
`;

export interface CompanyProfile {
  companyName?: string;
  phone?: string;
  phoneNumber?: string;
  email?: string;
  infoEmail?: string;
  address?: string;
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
  zipCode?: string;
  country?: string;
  logoUrl?: string;
  [key: string]: any;
}

export interface ReportForPdf {
  id?: string;
  status?: string;
  createdAt?: any;
  isManualEntry?: boolean;
  message?: string;
  user?: { name?: string; email?: string; role?: string };
  readerDetails?: {
    siteName?: string;
    device?: {
      deviceType?: string;
      modelName?: string;
      modelNumber?: string;
      specificLocation?: string;
      location?: string;
    };
  };
  location?: { address?: string };
  sitesDetails?: { name?: string; address?: string; photo?: string };
  shareData?: {
    images?: { uris?: Array<{ id?: string; img?: string }> };
    feedback?: { value?: string };
  };
  lawEnforcement?: {
    reported?: boolean;
    reportedToLawEnforcement?: boolean;
    department?: string;
    contactName?: string;
    contactPhone?: string;
    caseNumber?: string;
    reportedAt?: any;
  };
  caseNumber?: string;
  [key: string]: any;
}

function formatScanTime(report: ReportForPdf): string {
  let d: Date;
  const created = report.createdAt;
  if (created?.toDate && typeof created.toDate === 'function') {
    d = created.toDate();
  } else if (created && typeof created === 'object' && 'seconds' in created) {
    d = new Date((created as any).seconds * 1000);
  } else if (created) {
    d = new Date(created);
  } else {
    return 'Unknown';
  }
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatLawDate(law: ReportForPdf['lawEnforcement']): string {
  const at = law?.reportedAt;
  if (!at) return 'N/A';
  let d: Date;
  if (at?.toDate && typeof at.toDate === 'function') d = at.toDate();
  else if (at && typeof at === 'object' && 'seconds' in at) d = new Date((at as any).seconds * 1000);
  else d = new Date(at);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

export function buildMobileReportHtml(report: ReportForPdf, company: CompanyProfile | null): string {
  const companyName = company?.companyName || 'Company';
  const companyLogoUrl = company?.logoUrl || '';
  const contactLines: string[] = [];
  const addr = company?.address1 || company?.address || '';
  if (addr) contactLines.push(addr);
  const city = [company?.city, company?.state].filter(Boolean).join(', ');
  const zip = company?.zip || company?.zipCode || '';
  const country = company?.country || '';
  if ([city, zip, country].filter(Boolean).length) contactLines.push([city, zip, country].filter(Boolean).join(' '));
  if (company?.phone || company?.phoneNumber) contactLines.push(String(company?.phone || company?.phoneNumber));
  if (company?.email || company?.infoEmail) contactLines.push(String(company?.email || company?.infoEmail));
  const contactHtml = contactLines.length ? contactLines.map(escapeHtml).join('<br>') : '';

  const siteName = report.sitesDetails?.name || report.readerDetails?.siteName || 'Unknown Site';
  const siteAddress = report.sitesDetails?.address || report.location?.address || '';
  const siteAddressHtml = siteAddress ? escapeHtml(siteAddress) : '';
  const sitePhotoUri = report.sitesDetails?.photo || '';

  const userName = report.user?.name || 'Unknown';
  const userRole = report.user?.role || '';
  const userEmail = report.user?.email || '';
  const deviceType = report.readerDetails?.device?.deviceType || '';
  const modelName = report.readerDetails?.device?.modelName || '';
  const deviceSpecificLocation = report.readerDetails?.device?.specificLocation || report.readerDetails?.device?.location || '';

  const scanTime = formatScanTime(report);
  const status = (report.status || '').replace('!', '').trim();
  const isNoSkimmerDetected = status === 'No Skimmer Detected';
  const reportType = report.isManualEntry ? 'Manual Entry' : 'Device Scan';

  const reportId = report.id ? String(report.id).replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(-7).padStart(7, '0') : '0000000';

  const law = report.lawEnforcement;
  const lawReported = !!(law?.reportedToLawEnforcement ?? law?.reported);
  const showLawSection = status !== 'No Skimmer Detected';
  const lawDepartment = (law?.department ?? '').trim() ? escapeHtml(String(law?.department ?? '').trim()) : 'N/A';
  const lawContactName = (law?.contactName ?? '').trim() ? escapeHtml(String(law?.contactName ?? '').trim()) : 'N/A';
  const lawContactPhone = (law?.contactPhone ?? '').trim() ? escapeHtml(String(law?.contactPhone ?? '').trim()) : 'N/A';
  const lawCaseNumber = (law?.caseNumber ?? report.caseNumber ?? '').toString().trim() ? escapeHtml(String(law?.caseNumber ?? report.caseNumber ?? '').trim()) : 'N/A';
  const lawReportedDate = formatLawDate(law);

  const imagesUris = report.shareData?.images?.uris || [];
  const images = (imagesUris || []).map((u: any) => u?.img).filter((v: any) => typeof v === 'string' && v);
  const notesValue = (report.shareData?.feedback?.value || report.message || '').trim();
  const canShowImages = images.length > 0;
  const canShowNotes = true;

  const renderImagesGrid = () => {
    if (!images.length) return '';
    return `
    <div class="image-section">
      <h2>Skimmer Images</h2>
      <div class="image-grid">
        ${images.map((src: string) => `<div class="image-box"><img class="grid-photo" src="${escapeHtml(src)}" alt="Skimmer image"></div>`).join('')}
      </div>
    </div>`;
  };

  const compactClass = images.length > 0 && images.length <= 3 ? ' compact' : '';

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>${MOBILE_CSS}</style></head><body>
  <div class="page${compactClass}">
    <div class="header">
      <div class="logo-section">
        <div class="logo">${companyLogoUrl ? `<img src="${escapeHtml(companyLogoUrl)}" alt="Company logo">` : escapeHtml(companyName).slice(0, 1).toUpperCase()}</div>
        <div class="company-name">${escapeHtml(companyName)}</div>
      </div>
      <div class="contact-info">${contactHtml}</div>
    </div>
    <div class="main-content">
      <div class="report-header">
        <div class="report-heading">
          <div class="report-heading-content"><div class="report-title"><h1>Skimmer Detection Report</h1></div></div>
          <div class="report-separator"></div>
        </div>
        <div class="report-cards-row">
          <div class="site-card">
            <h2>Site Info</h2>
            <div class="site-card-row">
              ${sitePhotoUri ? `<img class="site-thumb" src="${escapeHtml(sitePhotoUri)}" alt="Site thumbnail">` : ''}
              <p>${escapeHtml(siteName)}<br>${siteAddressHtml || ''}</p>
            </div>
          </div>
          <div class="alert-box ${isNoSkimmerDetected ? 'ok' : ''}">
            <div class="alert-title ${isNoSkimmerDetected ? 'ok' : ''}">${escapeHtml(status === 'Skimmer Detected' ? 'Skimmer Detected' : status)}</div>
            <div class="alert-details">
              <div><strong>Scan Time:</strong><br>${escapeHtml(scanTime)}</div>
              <div><strong>Report Type:</strong> ${escapeHtml(reportType)}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="content-grid">
        <div class="site-card">
          <h2>User Info</h2>
          <p>${escapeHtml(userName)}<br>${userRole ? `<strong>Position:</strong> ${escapeHtml(userRole)}<br>` : ''}${userEmail ? `<strong>Email:</strong> ${escapeHtml(userEmail)}` : ''}</p>
        </div>
        <div class="site-card">
          <h2>Reader Info</h2>
          <p>${deviceSpecificLocation ? `<strong>Reader Location:</strong> ${escapeHtml(deviceSpecificLocation)}<br>` : ''}${deviceType ? `<strong>Reader Type:</strong> ${escapeHtml(deviceType)}<br>` : ''}${modelName ? `<strong>Reader Model:</strong> ${escapeHtml(modelName)}` : ''}</p>
        </div>
      </div>
      ${canShowImages ? renderImagesGrid() : ''}
      <div class="content-grid single">
        <div class="site-card full-width">
          <h2>Notes</h2>
          ${canShowNotes ? `<p>${escapeHtml(notesValue || 'N/A')}</p>` : '<p>N/A</p>'}
        </div>
      </div>
      ${showLawSection ? `
      <div class="notes-reporting">
        <label>
          <input type="checkbox" ${lawReported ? 'checked' : ''}>
          <span class="notes-reporting-text">
            ${lawReported ? `
            <strong>Reported to Law Enforcement</strong><br>
            <strong>Department:</strong> <span class="case-number-value">${lawDepartment}</span><br>
            <strong>Contact:</strong> <span class="case-number-value">${lawContactName}</span><br>
            <strong>Phone:</strong> <span class="case-number-value">${lawContactPhone}</span><br>
            <strong>Case Number:</strong> <span class="case-number-value">${lawCaseNumber}</span><br>
            <strong>Reported Date:</strong> <span class="case-number-value">${lawReportedDate}</span>
            ` : '<strong>Not Reported to Law Enforcement</strong>'}
          </span>
        </label>
      </div>
      ` : ''}
    </div>
    <div class="footer">
      <span>Detected by</span>
      <span>SkimBuster</span>
      <span class="footer-divider">|</span>
      <span>Report ID: ${escapeHtml(reportId)}</span>
      <span class="footer-site">micclo.com</span>
    </div>
  </div>
</body></html>`;
}
